/**
 * Deploy-time runtime configuration (DP-4750 / DSCO-5635).
 *
 * One build serves every stack and tenant. Nothing stack-specific is inlined at
 * build time: this module fetches `/config.json` from the app's own origin
 * before anything else runs (top-level await, so every importer — including
 * main.tsx — blocks until the document is resolved), validates it, and exports
 * plain constants. The synchronous export surface is unchanged for consumers.
 *
 * Fail closed: a missing, malformed, wrong-version, or wrong-tenant document
 * renders a hard error page and throws. There is no fallback to a baked
 * default — a dashboard pointed at the wrong stack is worse than a blank page.
 *
 * Local dev: `vite dev` / `vite preview` serve `/config.json` from a dev-only
 * middleware (see vite.config.ts, `DASH_CONFIG` / `DASH_CONFIG_OVERLAY`).
 * Unit tests inject a document through `globalThis.__ENCORE_RUNTIME_CONFIG__`
 * (vitest.setup.ts) because vitest serves nothing.
 */
import {
  CUSTOM_APP_SAME_ORIGIN_PATH,
  RuntimeConfigError,
  hostTenantFromHostname,
  validateRuntimeConfig,
  type RuntimeConfig,
} from './runtimeConfig';

export const RUNTIME_CONFIG_PATH = '/config.json';

/**
 * The two globals this module reads, typed without `declare global`:
 * - `__ENCORE_RUNTIME_CONFIG__` — test seam only, never set in a browser.
 * - `__encoreRuntimeConfig` — the early fetch started by an inline script in
 *   index.html so the document download overlaps the JS bundle download
 *   (see fetchRuntimeDocument).
 */
export interface RuntimeConfigGlobals {
  __ENCORE_RUNTIME_CONFIG__?: unknown;
  __encoreRuntimeConfig?: Promise<Response>;
}
const runtimeGlobals = globalThis as typeof globalThis & RuntimeConfigGlobals;

/**
 * The one request the app makes for its document. `no-cache` = always
 * revalidate with the edge (a 304 when unchanged): as fresh as `no-store`, and
 * it lets index.html start the request before the bundle has downloaded.
 * Edge freshness comes from the producer's `Cache-Control: no-cache,
 * must-revalidate`. Diverges from the HLD §5.4 wording (`no-store`) on
 * purpose — recorded in docs/runtime-config.md.
 */
export const RUNTIME_CONFIG_FETCH_INIT: RequestInit = {
  cache: 'no-cache',
  credentials: 'same-origin',
};

async function fetchRuntimeDocument(): Promise<unknown> {
  // Prefer the request index.html started at parse time. A rejected early
  // promise (network error) takes the same fail-closed path as a late fetch.
  const early = runtimeGlobals.__encoreRuntimeConfig;
  runtimeGlobals.__encoreRuntimeConfig = undefined;
  const response = await (early ??
    fetch(RUNTIME_CONFIG_PATH, RUNTIME_CONFIG_FETCH_INIT));
  if (!response.ok) {
    throw new RuntimeConfigError(
      `GET ${RUNTIME_CONFIG_PATH} returned HTTP ${response.status}`
    );
  }
  try {
    return await response.json();
  } catch {
    // A CDN rewriting unknown paths to index.html hands back HTML.
    throw new RuntimeConfigError(
      `GET ${RUNTIME_CONFIG_PATH} did not return JSON (content-type: ${
        response.headers.get('content-type') ?? 'unknown'
      })`
    );
  }
}

/**
 * Replace the boot spinner with a generic error page: the words of the app's
 * `GenericErrorPage` (title, message, Refresh button). The technical reason
 * stays in the console; users never see it.
 *
 * Built with the DOM API (never `innerHTML`) and with no imports: this runs
 * before the app exists, and in a built app every React / toolkit chunk goes
 * through the module-federation share scope that lives in the host chunk whose
 * top-level `await` is the very thing that just failed — so the real
 * `GenericErrorPage` component cannot render here. Same words, same button.
 */
function renderFatal(): void {
  const root = globalThis.document?.getElementById('root');
  if (!root) {
    return;
  }
  root.replaceChildren();
  const wrap = document.createElement('div');
  wrap.className = 'app-loader-container';
  wrap.setAttribute('role', 'alert');
  wrap.style.color = '#111827';
  wrap.style.textAlign = 'center';
  wrap.style.padding = '0 16px';

  const title = document.createElement('h1');
  title.textContent = 'Something went wrong';
  title.style.fontSize = '24px';
  title.style.fontWeight = '600';
  title.style.margin = '0 0 12px';

  const message = document.createElement('p');
  message.textContent =
    "We're sorry, but something unexpected happened. Please try refreshing the page.";
  message.style.color = '#6b7280';
  message.style.margin = '0 0 32px';
  message.style.maxWidth = '480px';

  const retry = document.createElement('button');
  retry.type = 'button';
  retry.textContent = 'Refresh Page';
  retry.style.cssText =
    'padding:10px 20px;border:0;border-radius:6px;background:#111827;color:#fff;font:inherit;font-weight:500;cursor:pointer';
  retry.addEventListener('click', () => window.location.reload());

  wrap.append(title, message, retry);
  root.append(wrap);
}

async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  try {
    const doc =
      runtimeGlobals.__ENCORE_RUNTIME_CONFIG__ ??
      (await fetchRuntimeDocument());
    const hostTenant = hostTenantFromHostname(
      globalThis.window?.location?.hostname,
      // Dev-only: local launchers serve from a "<tenant>-local" host.
      { stripLocalSuffix: import.meta.env.DEV }
    );
    return validateRuntimeConfig(doc, { hostTenant });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    // The only place the reason is shown. The page itself is generic.
    // eslint-disable-next-line no-console
    console.error('[encore-config] failing closed:', message);
    renderFatal();
    throw error;
  }
}

export const RUNTIME_CONFIG: Readonly<RuntimeConfig> = Object.freeze(
  await loadRuntimeConfig()
);

/** Stack name (was the build-time `VITE_APP_ENV`). */
export const STACK = RUNTIME_CONFIG.stack;
/** Tenant the document was rendered for, when the producer set it. */
export const TENANT = RUNTIME_CONFIG.tenant;

const API_BASE_DOMAIN = RUNTIME_CONFIG.apiBaseDomain;
const CUSTOM_APP_BASE_DOMAIN = RUNTIME_CONFIG.customAppBaseDomain;
const IS_CLIENT_FIRST_URL_SHAPE = RUNTIME_CONFIG.urlShape === 'client-first';
const CUSTOM_APP_REMOTES = RUNTIME_CONFIG.customAppRemotes;

/**
 * Constructs the API base URL for a tenant.
 * Format (service-first): https://api.{clientId}.{baseDomain}
 * Format (client-first):  https://{clientId}.{baseDomain}
 *
 * @param clientId - Tenant id. Empty or missing falls back to 'client'.
 * @returns The API base URL without a trailing slash.
 */
export function getApiBaseUrl(clientId: string = 'client'): string {
  const effectiveClientId = clientId?.trim() ? clientId : 'client';

  // Local backend: src/env used to carry "localhost:8081"; the dev document
  // keeps the same convention.
  if (
    API_BASE_DOMAIN === 'localhost' ||
    API_BASE_DOMAIN.startsWith('localhost:')
  ) {
    return `http://${API_BASE_DOMAIN}`;
  }

  return IS_CLIENT_FIRST_URL_SHAPE
    ? `https://${effectiveClientId}.${API_BASE_DOMAIN}`
    : `https://api.${effectiveClientId}.${API_BASE_DOMAIN}`;
}

/**
 * Resolves the base URL custom apps load from for the active tenant.
 *   no customAppBaseDomain (default, DP-6813): {location.origin}/encore-apps-studio
 *     — same origin as the dashboard; the edge maps the path to the tenant
 *     from the host, so clientId is not used.
 *   service-first: https://encore-apps-studio.{clientId}.{customAppBaseDomain}
 *   client-first:  https://{clientId}.{customAppBaseDomain}
 *
 * `studio` (DP-8112, `appConfig.studio`) names the repo serving the app and
 * replaces the `encore-apps-studio` label in the path / service-first shapes.
 * The client-first domain already embeds the service label, so it is unchanged.
 */
export function getCustomAppBaseUrl(
  clientId: string = 'client',
  studio?: string
): string {
  if (!CUSTOM_APP_BASE_DOMAIN) {
    const path = studio ? `/${studio}` : CUSTOM_APP_SAME_ORIGIN_PATH;
    return `${globalThis.location.origin}${path}`;
  }
  const effectiveClientId = clientId?.trim() || 'client';
  return IS_CLIENT_FIRST_URL_SHAPE
    ? `https://${effectiveClientId}.${CUSTOM_APP_BASE_DOMAIN}`
    : `https://${studio ?? 'encore-apps-studio'}.${effectiveClientId}.${CUSTOM_APP_BASE_DOMAIN}`;
}

/**
 * Resolves the Module Federation manifest URL for an MFE. A `customAppRemotes`
 * override in the document always wins over the computed URL (same-origin
 * path, or the cross-origin `customAppBaseDomain` edge when set).
 */
export function resolveRemoteEntryUrl(
  appName: string,
  clientId?: string,
  studio?: string
): string {
  return (
    CUSTOM_APP_REMOTES[appName] ??
    `${getCustomAppBaseUrl(clientId, studio)}/${appName}/mf-manifest.json`
  );
}

export const AUTH0_CUSTOM_CLAIMS_NAMESPACE =
  RUNTIME_CONFIG.auth0CustomClaimsNamespace;
export const AUTH0_DOMAIN = RUNTIME_CONFIG.auth.domain;
export const AUTH0_CLIENT_ID = RUNTIME_CONFIG.auth.clientId;
export const AUTH0_AUDIENCE = RUNTIME_CONFIG.auth.audience;

// Browser-side keys. Previously CircleCI `VITE_*` build args; now per stack in
// the document. Empty string keeps the "not configured" semantics consumers
// already check for.
export const GOOGLE_MAPS_API_KEY = RUNTIME_CONFIG.maps.googleMapsApiKey ?? '';
export const GROUNDCOVER_RUM_INGESTION_KEY =
  RUNTIME_CONFIG.observability.groundcoverRumIngestionKey ?? '';
export const GROUNDCOVER_RUM_DSN =
  RUNTIME_CONFIG.observability.groundcoverRumDsn ?? '';
export const BUGSNAG_API_KEY = RUNTIME_CONFIG.observability.bugsnagApiKey ?? '';
export const PENDO_API_KEY = RUNTIME_CONFIG.observability.pendoApiKey ?? '';
/** OpenTelemetry trace exporter (encore-observability telemetryService). */
export const OTLP_ENDPOINT = RUNTIME_CONFIG.observability.otlpEndpoint ?? '';
export const GROUNDCOVER_INGESTION_KEY =
  RUNTIME_CONFIG.observability.groundcoverIngestionKey ?? '';
